import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api, TariffAnalysisResponse, ReshoringAnalysisResponse } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import toast from 'react-hot-toast';

interface TariffReshoringTabsProps {
  productSlug: string;
  productName: string;
  isAnalyzing: boolean;
  initialTab?: string;
}

const TariffReshoringTabs = ({ productSlug, productName, isAnalyzing, initialTab = 'overview' }: TariffReshoringTabsProps) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [tariffData, setTariffData] = useState<TariffAnalysisResponse | null>(null);
  const [reshoringData, setReshoringData] = useState<ReshoringAnalysisResponse | null>(null);
  const [isLoadingTariff, setIsLoadingTariff] = useState(false);
  const [isLoadingReshoring, setIsLoadingReshoring] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Access control - Admin users have access to all features
  const hasBasicAccess = user?.role === 'admin' || (user?.subscription_tier && ['basic', 'premium', 'enterprise'].includes(user.subscription_tier));
  const hasPremiumAccess = user?.role === 'admin' || (user?.subscription_tier && ['premium', 'enterprise'].includes(user.subscription_tier));

  // Auto-load data when tab changes and user has access
  useEffect(() => {
    if (activeTab === 'tariff' && hasBasicAccess && !tariffData && !isAnalyzing) {
      loadTariffData();
    }
  }, [activeTab, hasBasicAccess]);

  useEffect(() => {
    if ((activeTab === 'reshoring' || activeTab === 'timeline') && hasPremiumAccess && !reshoringData && !isAnalyzing) {
      loadReshoringData();
    }
  }, [activeTab, hasPremiumAccess]);

  const loadTariffData = async () => {
    setIsLoadingTariff(true);
    try {
      const response = await api.getProductTariffAnalysis(productSlug);
      setTariffData(response.data);
    } catch (error) {
      console.error('Failed to load tariff data:', error);
      toast.error('Failed to load tariff analysis');
    } finally {
      setIsLoadingTariff(false);
    }
  };

  const loadReshoringData = async () => {
    setIsLoadingReshoring(true);
    try {
      const response = await api.getProductReshoringAnalysis(productSlug);
      setReshoringData(response.data);
    } catch (error) {
      console.error('Failed to load reshoring data:', error);
      toast.error('Failed to load reshoring analysis');
    } finally {
      setIsLoadingReshoring(false);
    }
  };

  const handleTabClick = (tab: string) => {
    // Check access levels and show upgrade prompts
    if (tab === 'tariff' && !hasBasicAccess) {
      setShowUpgradeModal(true);
      return;
    }
    if ((tab === 'reshoring' || tab === 'timeline') && !hasPremiumAccess) {
      setShowUpgradeModal(true);
      return;
    }
    setActiveTab(tab as any);
  };

  const TabButton = ({ 
    id, 
    label, 
    icon, 
    requiresUpgrade, 
    upgradeLevel 
  }: { 
    id: string; 
    label: string; 
    icon: string; 
    requiresUpgrade: boolean;
    upgradeLevel?: string;
  }) => (
    <button
      onClick={() => handleTabClick(id)}
      className={`relative px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
        activeTab === id
          ? 'bg-blue-600 text-white shadow-md'
          : requiresUpgrade
          ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
          : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
      }`}
    >
      <div className="flex items-center space-x-2">
        <span>{icon}</span>
        <span>{label}</span>
        {requiresUpgrade && (
          <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
            {upgradeLevel}
          </span>
        )}
      </div>
    </button>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab productName={productName} />;
      
      case 'tariff':
        if (!hasBasicAccess) return <UpgradePrompt level="Basic" />;
        return (
          <TariffTab 
            data={tariffData} 
            isLoading={isLoadingTariff} 
            onRefresh={loadTariffData}
          />
        );
      
      case 'reshoring':
        if (!hasPremiumAccess) return <UpgradePrompt level="Premium" />;
        return (
          <ReshoringTab 
            data={reshoringData} 
            isLoading={isLoadingReshoring} 
            onRefresh={loadReshoringData}
          />
        );
      
      case 'timeline':
        if (!hasPremiumAccess) return <UpgradePrompt level="Premium" />;
        return <TimelineTab data={reshoringData} />;
      
      default:
        return <OverviewTab productName={productName} />;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          🌐 Trade & Manufacturing Analysis
        </h2>
        <div className="flex space-x-2">
          <TabButton 
            id="overview" 
            label="Overview" 
            icon="📊" 
            requiresUpgrade={false}
          />
          <TabButton 
            id="tariff" 
            label="Tariff Impact" 
            icon="💰" 
            requiresUpgrade={!hasBasicAccess}
            upgradeLevel="Basic"
          />
          <TabButton 
            id="reshoring" 
            label="Reshoring Analysis" 
            icon="🏭" 
            requiresUpgrade={!hasPremiumAccess}
            upgradeLevel="Premium"
          />
          <TabButton 
            id="timeline" 
            label="Timeline" 
            icon="⏱️" 
            requiresUpgrade={!hasPremiumAccess}
            upgradeLevel="Premium"
          />
        </div>
      </div>

      {/* Analysis in Progress Banner */}
      {isAnalyzing && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full mr-3"></div>
            <div>
              <div className="font-medium text-blue-900">Analysis in Progress</div>
              <div className="text-sm text-blue-700">
                Tariff and reshoring data will be available once component analysis is complete.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {renderTabContent()}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal 
          onClose={() => setShowUpgradeModal(false)}
          currentTier={user?.subscription_tier}
        />
      )}
    </div>
  );
};

// Sub-components for each tab
const OverviewTab = ({ productName }: { productName: string }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg">
        <div className="flex items-center mb-3">
          <span className="text-2xl mr-3">💰</span>
          <h3 className="font-semibold text-gray-900">Tariff Impact</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          See how trade policies affect the cost of {productName} components.
        </p>
        <div className="text-xs text-blue-700 bg-blue-200 px-2 py-1 rounded">
          Requires Basic Plan
        </div>
      </div>

      <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg">
        <div className="flex items-center mb-3">
          <span className="text-2xl mr-3">🏭</span>
          <h3 className="font-semibold text-gray-900">Reshoring Analysis</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Explore feasibility and costs of moving production to different regions.
        </p>
        <div className="text-xs text-green-700 bg-green-200 px-2 py-1 rounded">
          Requires Premium Plan
        </div>
      </div>

      <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg">
        <div className="flex items-center mb-3">
          <span className="text-2xl mr-3">⏱️</span>
          <h3 className="font-semibold text-gray-900">Implementation Timeline</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Detailed roadmaps for production transitions and investment planning.
        </p>
        <div className="text-xs text-purple-700 bg-purple-200 px-2 py-1 rounded">
          Requires Premium Plan
        </div>
      </div>
    </div>
  </div>
);

const TariffTab = ({ 
  data, 
  isLoading, 
  onRefresh 
}: { 
  data: TariffAnalysisResponse | null; 
  isLoading: boolean; 
  onRefresh: () => void;
}) => {
  if (isLoading) {
    return <LoadingState message="Analyzing tariff impacts..." />;
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">🔄</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Tariff Analysis</h3>
        <p className="text-gray-600 mb-4">
          Click refresh to generate tariff impact analysis for this product.
        </p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Generate Analysis
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cost Impact Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tariff Cost Impact</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(data.total_tariff_cost_usd)}
            </div>
            <div className="text-sm text-gray-600">Total Tariff Cost</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              +{data.tariff_percentage}%
            </div>
            <div className="text-sm text-gray-600">Price Increase</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {data.component_breakdown.length}
            </div>
            <div className="text-sm text-gray-600">Components Affected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(data.policy_scenarios.trade_war.total_cost - data.policy_scenarios.current.total_cost)}
            </div>
            <div className="text-sm text-gray-600">Trade War Risk</div>
          </div>
        </div>
      </div>

      {/* Component Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Component Tariff Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Component</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">HTS Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Tariff Rate</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Tariff Cost</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Risk Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.component_breakdown.map((component, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {component.component_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {component.hts_code}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {component.applicable_rate}%
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-red-600">
                    +{formatCurrency(component.tariff_cost_usd)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      component.policy_risk === 'HIGH' ? 'bg-red-100 text-red-800' :
                      component.policy_risk === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {component.policy_risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Policy Scenarios */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Policy Scenarios</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">Base Case</h4>
            <div className="text-lg font-bold text-green-800">
              {formatCurrency(data.policy_scenarios.base_case.total_cost)}
            </div>
            <div className="text-sm text-green-600">
              {(data.policy_scenarios.base_case.probability * 100).toFixed(0)}% probability
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Current</h4>
            <div className="text-lg font-bold text-blue-800">
              {formatCurrency(data.policy_scenarios.current.total_cost)}
            </div>
            <div className="text-sm text-blue-600">
              {(data.policy_scenarios.current.probability * 100).toFixed(0)}% probability
            </div>
          </div>
          
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h4 className="font-medium text-orange-900 mb-2">Trade War</h4>
            <div className="text-lg font-bold text-orange-800">
              {formatCurrency(data.policy_scenarios.trade_war.total_cost)}
            </div>
            <div className="text-sm text-orange-600">
              {(data.policy_scenarios.trade_war.probability * 100).toFixed(0)}% probability
            </div>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-900 mb-2">Retaliation</h4>
            <div className="text-lg font-bold text-red-800">
              {formatCurrency(data.policy_scenarios.retaliation.total_cost)}
            </div>
            <div className="text-sm text-red-600">
              {(data.policy_scenarios.retaliation.probability * 100).toFixed(0)}% probability
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReshoringTab = ({ 
  data, 
  isLoading, 
  onRefresh 
}: { 
  data: ReshoringAnalysisResponse | null; 
  isLoading: boolean; 
  onRefresh: () => void;
}) => {
  if (isLoading) {
    return <LoadingState message="Analyzing reshoring feasibility..." />;
  }

  if (!data) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-4">🏭</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Reshoring Analysis</h3>
        <p className="text-gray-600 mb-4">
          Generate comprehensive reshoring feasibility analysis.
        </p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Generate Analysis
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Feasibility Overview */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reshoring Feasibility</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">
              {data.feasibility_assessment.feasibility_score}%
            </div>
            <div className="text-sm text-gray-600">Overall Feasibility</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">
              {data.financial_projections.roi_metrics.roi_5_year}%
            </div>
            <div className="text-sm text-gray-600">5-Year ROI</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">
              {formatCurrency(data.financial_projections.cost_difference)}
            </div>
            <div className="text-sm text-gray-600">Cost Impact</div>
          </div>
        </div>
      </div>

      {/* Component Feasibility */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Component Analysis</h3>
        <div className="space-y-3">
          {data.component_analysis.detailed_assessments.map((component, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900">{component.component_name}</div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${
                  component.feasibility_category === 'HIGH' ? 'bg-green-100 text-green-800' :
                  component.feasibility_category === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {component.feasibility_category} FEASIBILITY
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Current:</span> {formatCurrency(component.current_cost_usd)}
                </div>
                <div>
                  <span className="font-medium">Reshored:</span> {formatCurrency(component.reshored_cost_usd)}
                </div>
                <div>
                  <span className="font-medium">Timeline:</span> {component.transition_timeline_months} months
                </div>
                <div>
                  <span className="font-medium">Cost Impact:</span> +{component.cost_increase_percentage}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Financial Projections */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Projections</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Investment Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Facility:</span>
                <span className="font-medium">{formatCurrency(data.financial_projections.investment_breakdown.facility)}</span>
              </div>
              <div className="flex justify-between">
                <span>Equipment:</span>
                <span className="font-medium">{formatCurrency(data.financial_projections.investment_breakdown.equipment)}</span>
              </div>
              <div className="flex justify-between">
                <span>Training:</span>
                <span className="font-medium">{formatCurrency(data.financial_projections.investment_breakdown.training)}</span>
              </div>
              <div className="flex justify-between">
                <span>Working Capital:</span>
                <span className="font-medium">{formatCurrency(data.financial_projections.investment_breakdown.working_capital)}</span>
              </div>
              <div className="flex justify-between border-t pt-2 font-bold">
                <span>Total:</span>
                <span>{formatCurrency(data.financial_projections.investment_breakdown.total)}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-3">ROI Metrics</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Payback Period:</span>
                <span className="font-medium">{data.financial_projections.roi_metrics.payback_period_months} months</span>
              </div>
              <div className="flex justify-between">
                <span>5-Year ROI:</span>
                <span className="font-medium">{data.financial_projections.roi_metrics.roi_5_year}%</span>
              </div>
              <div className="flex justify-between">
                <span>NPV (5-year):</span>
                <span className="font-medium">{formatCurrency(data.financial_projections.roi_metrics.npv_5_year)}</span>
              </div>
              <div className="flex justify-between">
                <span>IRR:</span>
                <span className="font-medium">{data.financial_projections.roi_metrics.irr}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TimelineTab = ({ data }: { data: ReshoringAnalysisResponse | null }) => {
  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">⏱️</div>
        <div>Generate reshoring analysis first to see timeline</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Implementation Timeline</h3>
      
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="text-center mb-6">
          <div className="text-3xl font-bold text-blue-600">
            {data.implementation_timeline.total_months} months
          </div>
          <div className="text-sm text-gray-600">Total Implementation Time</div>
        </div>
        
        <div className="space-y-4">
          {data.implementation_timeline.phases.map((phase, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-medium text-gray-900">
                  Phase {index + 1}: {phase.phase}
                </h4>
                <div className="text-sm text-gray-600">
                  {phase.months} months
                </div>
              </div>
              
              {/* Progress bar visualization */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full" 
                  style={{ width: `${(phase.months / data.implementation_timeline.total_months) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Risk Categories</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Technical:</span>
                <span className="font-medium">{data.risk_assessment.risk_categories.technical}</span>
              </div>
              <div className="flex justify-between">
                <span>Financial:</span>
                <span className="font-medium">{data.risk_assessment.risk_categories.financial}</span>
              </div>
              <div className="flex justify-between">
                <span>Market:</span>
                <span className="font-medium">{data.risk_assessment.risk_categories.market}</span>
              </div>
              <div className="flex justify-between">
                <span>Regulatory:</span>
                <span className="font-medium">{data.risk_assessment.risk_categories.regulatory}</span>
              </div>
              <div className="flex justify-between">
                <span>Talent:</span>
                <span className="font-medium">{data.risk_assessment.risk_categories.talent}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-700 mb-3">Success Factors</h4>
            <div className="space-y-2 text-sm">
              {data.risk_assessment.success_factors.slice(0, 5).map((factor, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>{factor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoadingState = ({ message }: { message: string }) => (
  <div className="flex items-center justify-center py-12">
    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mr-4"></div>
    <span className="text-gray-600">{message}</span>
  </div>
);

const UpgradePrompt = ({ level }: { level: string }) => (
  <div className="text-center py-12">
    <div className="text-6xl mb-4">🔒</div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">
      Upgrade to {level} Plan
    </h3>
    <p className="text-gray-600 mb-6">
      Access detailed {level.toLowerCase()} analysis features with our {level} plan.
    </p>
    <button 
      onClick={() => {
        window.location.href = `/upgrade?plan=${level.toLowerCase()}`;
      }}
      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Upgrade Now
    </button>
  </div>
);

const UpgradeModal = ({ 
  onClose, 
  currentTier 
}: { 
  onClose: () => void; 
  currentTier?: string;
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Upgrade Required</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          ✕
        </button>
      </div>
      
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          You're currently on the {currentTier || 'Free'} plan. 
          Upgrade to access advanced tariff and reshoring analysis features.
        </div>
        
        <div className="grid grid-cols-1 gap-3">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Basic Plan</span>
              <span className="text-lg font-bold text-gray-900">$19/mo</span>
            </div>
            <div className="text-sm text-gray-600 mb-3">
              • Tariff impact analysis
              • Policy scenario modeling
              • Component tariff breakdown
            </div>
            <button 
              onClick={() => {
                window.location.href = '/upgrade?plan=basic';
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upgrade to Basic
            </button>
          </div>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-gray-900">Premium Plan</span>
              <span className="text-lg font-bold text-gray-900">$49/mo</span>
            </div>
            <div className="text-sm text-gray-600 mb-3">
              • Everything in Basic
              • Reshoring feasibility analysis
              • Investment ROI calculations
              • Advanced policy modeling
            </div>
            <button 
              onClick={() => {
                window.location.href = '/upgrade?plan=premium';
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upgrade to Premium
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default TariffReshoringTabs; 